import { createQueryKeys } from "@lukemorales/query-key-factory";
import { http } from "src/axios";
import type { Scope } from "src/pages/UserDashboard/Customers/CustomersCreate/ScopesTable/types";

export interface AccountsApiResponse {
  pages: number;
  stores: AccountStore[];
  success: boolean;
  total_records: number;
}

export interface AccountStore {
  "Account Config Vendor Name": string | null;
  "Account ID": number;
  "Account Name": string;
}

async function getAccounts({ signal }: { signal?: AbortSignal }): Promise<AccountsApiResponse> {
  const response = await http.get<AccountsApiResponse>("/api/v1/accounts", { signal });
  return response.data;
}

export interface AccountWithScopeDeactivationDatesResponse {
  ACCOUNTS: AccountWithScopeDeactivationDates[];
}

export interface ScopeDeactivationDates {
  CHARGEBACKS: string | null;
  SHORTAGES: string | null;
  PRICE_CLAIMS: string | null;
  ACCRUALS: string | null;
  VENDOR_RETURNS: string | null;
}

export interface AccountWithScopeDeactivationDates {
  ID: number;
  NAME: string;
  IS_ACTIVE: boolean;
  DISPLAY_NAME: string;
  SCOPE_DEACTIVATION_DATES: ScopeDeactivationDates;
}

async function getAccountsWithScopeDeactivationDates({
  signal,
}: {
  signal?: AbortSignal;
}): Promise<AccountWithScopeDeactivationDatesResponse> {
  const response = await http.get<{ data: AccountWithScopeDeactivationDatesResponse }>(
    "/api/v2/accounts?include=scope_deactivation_dates",
    { signal },
  );
  return response.data.data;
}

async function getAccountScopes(accountId: string | number, signal?: AbortSignal): Promise<Scope[]> {
  const response = await http.get<{ scopes: Scope[] }>(`/api/v1/accounts/${accountId}/scopes`, { signal });
  return response.data.scopes;
}

export const accounts = createQueryKeys("accounts", {
  list: {
    queryKey: null,
    queryFn: getAccounts,
  },
  listWithScopeDeactivationDates: {
    queryKey: null,
    queryFn: getAccountsWithScopeDeactivationDates,
  },
  detail: (accountId: string | number) => ({
    // no detail function, context queries only
    queryKey: [accountId],
    contextQueries: {
      scopes: {
        // TODO(daniel): file an issue with @tanstack/eslint-plugin-query, query key should be null here
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: null,
        queryFn: ({ signal }) => getAccountScopes(accountId, signal),
      },
    },
  }),
});
